import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from "react";
import { AccedentById, accidentStartWith } from "../../../api/blackListApi";
import { getDropdownData, getOtherRefDetails } from "../../../api/keyvalue";
import LoadingSpinner from "../../LoadingSpinner";
import Dropdown from "../../input/Dropdown";
import Input from "../../input/Input";
import SearchDropdown from "../../input/SearchDropdown";
import { blackListValidation } from "../../validations/BlackListCreation";
import InfoList from "../../InfoList";
import { formatFromJsonDate, toDayJs, isAfterJson } from "../../../dateConfig";
import { User } from "react-feather";
import { FORMS, MODULES } from "../../../const";
import useUserCompany from "../../../hooks/useUserCompany";
import PopUpDateTimePicker from "../../input/PopUpDateTimePicker";
import Modal from "../../Modal";

const FORM = FORMS.BLACKLIST.BLK_DETAIL.form;

const BlackListForm = forwardRef(
    (
        {
            errors,
            dropdown,
            isLoading,
            currentValues,
            editMode,
            canUnblock,
            isRecordAlreadyUnblocked,
        },
        ref
    ) => {
        const today = new Date().toISOString().split("T")[0];
        const userCompany = useUserCompany();
        const [blkTypeOptions, setBlkTypeOptions] = useState([]);

        const [company, setCompany] = useState(
            userCompany ? userCompany.comId : null
        );

        useEffect(() => {
            setCompany(userCompany ? userCompany.comId : null);
        }, [userCompany]);

        const [typeDisabled, setTypeDisabled] = useState(true);
        const [blackListData, setBlackListData] = useState(
            editMode
                ? {
                    ...currentValues,
                    blkFrom: currentValues?.blkFrom,
                    blkTo: currentValues?.blkTo,
                    blkReferance: currentValues?.blkReferance,
                    blkAccident: currentValues?.blkAccident,
                    minToDate: currentValues?.blkFrom,
                }
                : {
                    blkCode: "",
                    blkCompany: company,
                    blkType: "",
                    blkCategory: "",
                    blkReferance: "",
                    blkAccident: "",
                    blkDescription: "",
                    blkAction: 1,
                    blkFrom: "",
                    blkTo: "",
                    minToDate: today,
                    blkStatus: 2,
                    blkActive: 1,
                }
        );

        const [currentErrors, setCurrentErrors] = useState({});
        const [searchFieldReset, setSearchFieldReset] = useState(false);
        const [vehEmpReset, setVehEmpReset] = useState(false);
        const [accidentReset, setAccidentReset] = useState(false);

        const modalRef = useRef();

        const POPUP = { FROM: "FROM", TO: "TO" };

        const [popUpContent, setPopUpContent] = useState(null);
        const [popUpKey, setPopUpKey] = useState(1);

        const [currentFromDateTime, setCurrentFromDateTime] = useState(null);
        const [currentToDateTime, setCurrentToDateTime] = useState(null);

        const openFromPicker = () => {
            if (editMode) return;

            setPopUpContent(POPUP.FROM);
            setCurrentFromDateTime(
                blackListData.blkFrom ? toDayJs(blackListData.blkFrom) : null
            );
            setPopUpKey((k) => k + 1);
            modalRef.current?.showModal();
        };

        const openToPicker = () => {
            if (editMode) return;
            if (!blackListData.blkFrom) return;

            setPopUpContent(POPUP.TO);
            setCurrentFromDateTime(toDayJs(blackListData.blkFrom));
            setCurrentToDateTime(
                blackListData.blkTo ? toDayJs(blackListData.blkTo) : null
            );
            setPopUpKey((k) => k + 1);
            modalRef.current?.showModal();
        };

        const acceptFrom = (value) => {
            setBlackListData((prev) => ({ ...prev, blkFrom: value, blkTo: "" }));
            modalRef.current?.close();
        };

        const acceptTo = (value) => {
            setBlackListData((prev) => ({ ...prev, blkTo: value }));
            modalRef.current?.close();
        };

        let popUpComponent = <></>;

        if (popUpContent === POPUP.FROM) {
            popUpComponent = (
                <PopUpDateTimePicker
                    key={popUpKey}
                    value={currentFromDateTime}
                    onAccept={acceptFrom}
                    onCancel={() => modalRef.current?.close()}
                    returnJsonDate={true}
                />
            );
        }

        if (popUpContent === POPUP.TO) {
            popUpComponent = (
                <PopUpDateTimePicker
                    key={popUpKey}
                    value={currentToDateTime}
                    onAccept={acceptTo}
                    onCancel={() => modalRef.current?.close()}
                    returnJsonDate={true}
                    validators={[
                        {
                            func: (date) =>
                                !blackListData.blkFrom
                                    ? true
                                    : isAfterJson(date, toDayJs(blackListData.blkFrom)),
                            message: "End date/time must be after Start date/time",
                        },
                    ]}
                />
            );
        }

        useEffect(() => {
            setCurrentErrors(errors);
        }, [errors]);

        useImperativeHandle(ref, () => ({
            getData: () => blackListData,
            validateForm: () => {
                return blackListValidation(blackListData);
            },
            resetForm: () => {
                setBlackListData({
                    blkCode: "",
                    blkType: "",
                    blkCategory: "",
                    blkReferance: "",
                    blkAccident: "",
                    blkDescription: "",
                    blkAction: 1,
                    blkFrom: "",
                    blkTo: "",
                    blkStatus: 2,
                    blkActive: 1,
                });
                setSearchFieldReset(true);
                setVehEmpReset(true);
                setCurrentErrors({});
            },
        }));

        const [currentBlkAccidentValue, setCurrentBlkAccidentValue] = useState("");

        useEffect(() => {
            (async () => {
                if (editMode && blackListData.blkAccident) {
                    const blkAccident = await AccedentById(blackListData.blkAccident);
                    setCurrentBlkAccidentValue(blkAccident);
                    const groupValue = blackListData.blkCategory === 1 ? "1" : "2";
                    const typeOptions = await getDropdownData("blk_type", groupValue);
                    setBlkTypeOptions(typeOptions);

                    setTypeDisabled(false);

                    if (currentValues?.blkType) {
                        setBlackListData((prevState) => ({
                            ...prevState,
                            blkType: currentValues.blkType,
                        }));
                    }
                }
            })();
        }, [editMode]);

        const handleCategoryChange = async (e, data) => {
            setBlackListData((prevState) => ({
                ...prevState,
                blkCategory: data.value,
                blkType: "",
            }));

            const groupValue = data.value === 1 ? 1 : 2;
            const typeOptions = await getDropdownData("blk_type", groupValue);
            setBlkTypeOptions(typeOptions);

            setTypeDisabled(false);
        };

        return (
            <>
                {isLoading ? (
                    <LoadingSpinner />
                ) : (
                    <form
                        className="ui form"
                        style={{ overflowY: "auto", height: "75vh" }}
                    >
                        <Modal ref={modalRef} minWidth="0px">
                            {popUpComponent}
                        </Modal>
                        <div className="ui segment">
                            <h4 className="ui dividing header">Black List</h4>
                            <div className="field">
                                <Input
                                    label="Black List code"
                                    error={currentErrors.blkCode}
                                    module={MODULES.Master}
                                    form={FORM}
                                    field="blk_code"
                                    editMode={editMode}
                                    resetError={() =>
                                        setCurrentErrors((prevState) => {
                                            const { blkCode: _, ...rest } = prevState;
                                            return rest;
                                        })
                                    }
                                    name="employeeCode"
                                    value={blackListData.blkCode}
                                    onChange={(e) => {
                                        setBlackListData((prevState) => ({
                                            ...prevState,
                                            blkCode: e.target.value,
                                        }));
                                    }}
                                    disabled={true}
                                />
                            </div>
                            <div className="two fields">
                                <Dropdown
                                    readOnly={editMode}
                                    required={true}
                                    label="Category"
                                    error={currentErrors.blkCategory}
                                    module={MODULES.Master}
                                    form={FORM}
                                    field="blk_category"
                                    editMode={editMode}
                                    resetError={() =>
                                        setCurrentErrors((prevState) => {
                                            const { blkCategory: _, ...rest } = prevState;
                                            return rest;
                                        })
                                    }
                                    placeholder="Type"
                                    options={dropdown.blkCategoryOptions}
                                    value={blackListData.blkCategory}
                                    style={{ height: "20px" }}
                                    onChange={handleCategoryChange}
                                />
                                <SearchDropdown
                                    key={1}
                                    readOnly={editMode}
                                    editMode={editMode}
                                    module={MODULES.Master}
                                    form={FORM}
                                    field="blk_referance"
                                    reset={vehEmpReset}
                                    setReset={setVehEmpReset}
                                    clearable={true}
                                    onClear={() => {
                                        setBlackListData((prevState) => ({
                                            ...prevState,
                                            blkReferance: null,
                                        }));
                                    }}
                                    onResultSelect={(data) =>
                                        setBlackListData((prevState) => ({
                                            ...prevState,
                                            blkReferance: data,
                                        }))
                                    }
                                    api={getOtherRefDetails}
                                    apiDependency={
                                        blackListData.blkCategory === 1
                                            ? {
                                                type: "vehicle",
                                                value: "",
                                            }
                                            : {
                                                type: "employee",
                                                value: "Driver",
                                            }
                                    }
                                    fieldInfo={
                                        blackListData.blkCategory === 1
                                            ? {
                                                tableName: "masterdata.tms_m_vehicle_details",
                                                primaryKey: "veh_id",
                                                fieldName: "veh_vehicleno",
                                                id: blackListData.blkReferance,
                                            }
                                            : {
                                                tableName: "masterdata.tms_m_employee",
                                                primaryKey: "emp_id",
                                                fieldName: "emp_fullname",
                                                id: blackListData.blkReferance,
                                            }
                                    }
                                    required={true}
                                    label="Employee/Vehicle"
                                    error={currentErrors.blkReferance}
                                    resetError={() =>
                                        setCurrentErrors((prevState) => {
                                            const { blkReferance: _, ...rest } = prevState;
                                            return rest;
                                        })
                                    }
                                />
                            </div>
                            <div className="two fields">
                                <Dropdown
                                    required={true}
                                    label="Type"
                                    error={currentErrors.blkType}
                                    module={MODULES.Master}
                                    form={FORM}
                                    field="blk_type"
                                    editMode={editMode}
                                    resetError={() =>
                                        setCurrentErrors((prevState) => {
                                            const { blkType: _, ...rest } = prevState;
                                            return rest;
                                        })
                                    }
                                    placeholder="Type"
                                    options={blkTypeOptions}
                                    value={blackListData.blkType}
                                    style={{ height: "20px" }}
                                    onChange={(e, data) => {
                                        setBlackListData((prevState) => ({
                                            ...prevState,
                                            blkType: data.value,
                                        }));
                                    }}
                                    disabled={typeDisabled || blkTypeOptions.length === 0}
                                    readOnly={editMode}
                                />

                                <SearchDropdown
                                    key={1}
                                    editMode={editMode}
                                    reset={accidentReset}
                                    setReset={setAccidentReset}
                                    clearable={true}
                                    module={MODULES.Master}
                                    form={FORM}
                                    field="blk_accident"
                                    onClear={() => {
                                        setBlackListData((prevState) => ({
                                            ...prevState,
                                            blkAccident: null,
                                        }));
                                    }}
                                    onResultSelect={(data) =>
                                        setBlackListData((prevState) => ({
                                            ...prevState,
                                            blkAccident: data,
                                        }))
                                    }
                                    multiGrid={true}
                                    multiGridCurrentValue={currentBlkAccidentValue}
                                    api={accidentStartWith}
                                    label="Accident"
                                    error={currentErrors.blkAccident}
                                    resetError={() =>
                                        setCurrentErrors((prevState) => {
                                            const { blkAccident: _, ...rest } = prevState;
                                            return rest;
                                        })
                                    }
                                    disabled={
                                        !(
                                            blackListData.blkType === 1 || blackListData.blkType === 3
                                        )
                                    }
                                    readOnly={editMode}
                                />
                            </div>
                            <div className="two fields">
                                <Input
                                    label="Description"
                                    error={currentErrors.blkDescription}
                                    module={MODULES.Master}
                                    form={FORM}
                                    field="blk_description"
                                    editMode={editMode}
                                    resetError={() =>
                                        setCurrentErrors((prevState) => {
                                            const { blkDescription: _, ...rest } = prevState;
                                            return rest;
                                        })
                                    }
                                    placeholder="Description"
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                    }}
                                    value={blackListData.blkDescription}
                                    onChange={(e) => {
                                        setBlackListData((prevState) => ({
                                            ...prevState,
                                            blkDescription: e.target.value,
                                        }));
                                    }}
                                    disabled={editMode}
                                />

                                <Dropdown
                                    placeholder="Action"
                                    options={dropdown.blkActionOptions.filter((opt) =>
                                        opt.value === 2 ? canUnblock : true
                                    )}
                                    value={blackListData.blkAction}
                                    module={MODULES.Master}
                                    form={FORM}
                                    field="blk_action"
                                    editMode={editMode}
                                    style={{ height: "20px" }}
                                    disabled={isRecordAlreadyUnblocked || !canUnblock}
                                    onChange={(e, data) => {
                                        setBlackListData((prevState) => ({
                                            ...prevState,
                                            blkAction: data.value,
                                        }));
                                    }}
                                    error={currentErrors.blkAction}
                                    resetError={() =>
                                        setCurrentErrors((prevState) => {
                                            const { blkAction: _, ...rest } = prevState;
                                            return rest;
                                        })
                                    }
                                    label="Action"
                                />
                            </div>
                            <div className="two fields">
                                <div
                                    className="field"
                                    onClick={openFromPicker}
                                    style={{ cursor: editMode ? "not-allowed" : "pointer" }}
                                >
                                    <Input
                                        required={true}
                                        label="Start"
                                        type="text"
                                        placeholder="Select start date & time"
                                        value={
                                            blackListData.blkFrom
                                                ? formatFromJsonDate(blackListData.blkFrom)
                                                : ""
                                        }
                                        readOnly={true}
                                        disabled={editMode}
                                    />
                                </div>

                                <div
                                    className="field"
                                    onClick={openToPicker}
                                    style={{ cursor: editMode ? "not-allowed" : "pointer" }}
                                >
                                    <Input
                                        required={true}
                                        label="End"
                                        type="text"
                                        placeholder="Select end date & time"
                                        value={
                                            blackListData.blkTo
                                                ? formatFromJsonDate(blackListData.blkTo)
                                                : ""
                                        }
                                        readOnly={true}
                                        disabled={editMode}
                                    />
                                </div>
                            </div>
                        </div>
                        {editMode && (
                            <InfoList
                                title="User Information"
                                content={[
                                    {
                                        title: "Created By",
                                        value: blackListData.createdBy
                                            ? blackListData.createdBy
                                            : "N/A",
                                    },
                                    {
                                        title: "Created Date",
                                        value: blackListData.createdDate
                                            ? formatFromJsonDate(blackListData.createdDate)
                                            : "N/A",
                                    },
                                    {
                                        title: "Last Modified By",
                                        value: blackListData.lastModifiedBy
                                            ? blackListData.lastModifiedBy
                                            : "N/A",
                                    },
                                    {
                                        title: "Last Modified Date",
                                        value: blackListData.lastModifiedDate
                                            ? formatFromJsonDate(blackListData.lastModifiedDate)
                                            : "N/A",
                                    },
                                ]}
                                Icon={User}
                            />
                        )}
                    </form>
                )}
            </>
        );
    }
);

export default BlackListForm;